import { useCallback } from 'react'
import {
  loginCustomer,
  requestCustomerLoginPasskeyOptions,
  signupCustomer,
  verifyCustomerLoginPasskey,
  verifyCustomerLoginSecondFactor,
} from '../../customerAuthApi'
import type {
  CustomerLoginInput,
  CustomerSignupInput,
} from '../../types'
import { createPasskeyAuthenticationResponse } from '../../passkeyWebauthn'
import { toLoginPayload, toSignupPayload } from './payloads'
import { runBusyAuthAction } from './runAuthAction'
import type {
  ApplyAuthenticatedStateFn,
  CustomerAuthActionState,
  CustomerLoginActionResult,
} from './actionTypes'

type Params = CustomerAuthActionState & {
  applyAuthenticatedState: ApplyAuthenticatedStateFn
  mergeGuestCartAfterAuthentication: (nextToken: string) => Promise<void>
}

export const useCustomerLoginActions = ({
  applyAuthenticatedState,
  mergeGuestCartAfterAuthentication,
  setBusy,
  setError,
}: Params) => {
  const signup = useCallback(
    async (input: CustomerSignupInput) =>
      runBusyAuthAction({
        setBusy,
        setError,
        action: async () => {
          await signupCustomer(toSignupPayload(input))
          return true
        },
        onErrorResult: false,
      }),
    [setBusy, setError],
  )

  const login = useCallback(
    async (input: CustomerLoginInput) =>
      runBusyAuthAction({
        setBusy,
        setError,
        action: async () => {
          const payload = await loginCustomer(toLoginPayload(input))
          if ('requiresTwoFactor' in payload && payload.requiresTwoFactor) {
            return {
              kind: 'two_factor_required',
              session: payload,
            } as CustomerLoginActionResult
          }
          applyAuthenticatedState(payload.token, payload.account, payload.profile)
          await mergeGuestCartAfterAuthentication(payload.token)
          return { kind: 'authenticated' } as CustomerLoginActionResult
        },
        onErrorResult: { kind: 'failed' } satisfies CustomerLoginActionResult,
      }),
    [applyAuthenticatedState, mergeGuestCartAfterAuthentication, setBusy, setError],
  )

  const verifyLoginSecondFactor = useCallback(
    async (input: { sessionId: string; code: string }) =>
      runBusyAuthAction({
        setBusy,
        setError,
        action: async () => {
          const payload = await verifyCustomerLoginSecondFactor(input)
          applyAuthenticatedState(payload.token, payload.account, payload.profile)
          await mergeGuestCartAfterAuthentication(payload.token)
          return { kind: 'authenticated' } as CustomerLoginActionResult
        },
        onErrorResult: { kind: 'failed' } satisfies CustomerLoginActionResult,
      }),
    [applyAuthenticatedState, mergeGuestCartAfterAuthentication, setBusy, setError],
  )

  const verifyLoginSecondFactorWithPasskey = useCallback(
    async (input: { sessionId: string }) =>
      runBusyAuthAction({
        setBusy,
        setError,
        action: async () => {
          const options = await requestCustomerLoginPasskeyOptions(input.sessionId)
          const response = await createPasskeyAuthenticationResponse(options)
          const payload = await verifyCustomerLoginPasskey({
            sessionId: input.sessionId,
            response,
          })
          applyAuthenticatedState(payload.token, payload.account, payload.profile)
          await mergeGuestCartAfterAuthentication(payload.token)
          return { kind: 'authenticated' } as CustomerLoginActionResult
        },
        onErrorResult: { kind: 'failed' } satisfies CustomerLoginActionResult,
      }),
    [applyAuthenticatedState, mergeGuestCartAfterAuthentication, setBusy, setError],
  )

  return {
    signup,
    login,
    verifyLoginSecondFactor,
    verifyLoginSecondFactorWithPasskey,
  }
}
